import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * NavigationMenu — top-level horizontal menu with hover-/click-revealed
 * panels. Used for application shells and marketing-style nav bars where
 * each top item expands to a richer flyout (links grouped by category,
 * featured cards, etc).
 *
 * Built on Radix Navigation Menu so we inherit:
 *   - Hover delay + intent detection
 *   - Keyboard navigation
 *   - aria-current and aria-expanded wiring
 *
 * The Indicator + Viewport pattern is preserved so the chevron rail can
 * follow the active trigger.
 */

export const NavigationMenu = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(function NavigationMenu({ children, className, ...props }, ref) {
  return (
    <NavigationMenuPrimitive.Root
      ref={ref}
      className={cx("cw-v2-navigation-menu", className)}
      {...props}
    >
      {children}
      <NavigationMenuPrimitive.Viewport className="cw-v2-navigation-menu__viewport" />
    </NavigationMenuPrimitive.Root>
  );
});
NavigationMenu.displayName = "NavigationMenu";

export const NavigationMenuList = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(function NavigationMenuList({ className, ...props }, ref) {
  return (
    <NavigationMenuPrimitive.List
      ref={ref}
      className={cx("cw-v2-navigation-menu__list", className)}
      {...props}
    />
  );
});
NavigationMenuList.displayName = "NavigationMenuList";

export const NavigationMenuItem = NavigationMenuPrimitive.Item;

export const NavigationMenuTrigger = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger>
>(function NavigationMenuTrigger({ children, className, ...props }, ref) {
  return (
    <NavigationMenuPrimitive.Trigger
      ref={ref}
      className={cx("cw-v2-navigation-menu__trigger", className)}
      {...props}
    >
      {children}
      <ChevronDown className="cw-v2-navigation-menu__chevron" aria-hidden />
    </NavigationMenuPrimitive.Trigger>
  );
});
NavigationMenuTrigger.displayName = "NavigationMenuTrigger";

export const NavigationMenuContent = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(function NavigationMenuContent({ className, ...props }, ref) {
  return (
    <NavigationMenuPrimitive.Content
      ref={ref}
      className={cx("cw-v2-navigation-menu__content", className)}
      {...props}
    />
  );
});
NavigationMenuContent.displayName = "NavigationMenuContent";

export const NavigationMenuLink = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Link>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Link>
>(function NavigationMenuLink({ className, ...props }, ref) {
  return (
    <NavigationMenuPrimitive.Link
      ref={ref}
      className={cx("cw-v2-navigation-menu__link", className)}
      {...props}
    />
  );
});
NavigationMenuLink.displayName = "NavigationMenuLink";

export const NavigationMenuViewport = NavigationMenuPrimitive.Viewport;

export const NavigationMenuIndicator = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Indicator>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator>
>(function NavigationMenuIndicator({ className, ...props }, ref) {
  return (
    <NavigationMenuPrimitive.Indicator
      ref={ref}
      className={cx("cw-v2-navigation-menu__indicator", className)}
      {...props}
    >
      <span className="cw-v2-navigation-menu__indicator-arrow" />
    </NavigationMenuPrimitive.Indicator>
  );
});
NavigationMenuIndicator.displayName = "NavigationMenuIndicator";
